import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import { useCallback } from 'react';
import { ViewMode } from './ViewModeToggle';

interface ViewModeBinding {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}

/**
 * Bindings for the grid/list toggles, spread straight into `ViewModeToggle` or
 * `MobileViewModeButton`:
 *
 * ```tsx
 * <ViewModeToggle {...useLibrariesViewMode()} />
 * ```
 *
 * The toggles are controlled because the two pages persist to different preference
 * keys and fire different events. That pushed an identical `track(); set()` pair
 * into four call sites — two per page, one desktop and one mobile — where a change
 * to one surface's tracking would silently leave the other disagreeing. One hook per
 * page keeps "which key, which event" in a single place.
 */
export function useLibraryViewMode(): ViewModeBinding {
  const uiPreferences = useUIPreferences();
  return {
    value: uiPreferences.libraryViewMode,
    onChange: useCallback(
      (mode: ViewMode) => {
        trackingStore.libraryViewModeChanged({ mode });
        uiPreferences.setLibraryViewMode(mode);
      },
      [uiPreferences],
    ),
  };
}

/** As above, for the Libraries page (the list of libraries). */
export function useLibrariesViewMode(): ViewModeBinding {
  const uiPreferences = useUIPreferences();
  return {
    value: uiPreferences.librariesViewMode,
    onChange: useCallback(
      (mode: ViewMode) => {
        trackingStore.librariesViewModeChanged({ mode });
        uiPreferences.setLibrariesViewMode(mode);
      },
      [uiPreferences],
    ),
  };
}
